// Phase 5.4 — Preview & Approve screen with editable layout (60/40 split).
//
// LEFT (60%): base image + regenerate-image button.
// RIGHT (40%): editable style dropdown + editable script textarea.
// FOOTER: Descartar / Crear mi video.
//
// State machine consumed from useVideoDraftStore:
//   - "directing"          → director Gemini running, show loader
//   - "ready"               → editable preview (the main screen)
//   - "editing_script"     → edit-script in flight
//   - "regenerating_image" → regenerate-image in flight
//   - "regenerating_style" → regenerate-style in flight (then directing)
//   - "approving"          → approve in flight, button disabled
//   - "rejecting"          → reject in flight, button disabled
//   - "rendering"          → post-approve, base image visible + progress
//   - "finished"           → final video player + Listo button
//   - "done"               → terminal (post-reject), auto-close
//   - "error"              → error screen with retry

import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle2,
  Loader2,
  Pencil,
  RefreshCw,
  Sparkles,
  X,
  XCircle,
} from "lucide-react";
import {
  PATTERN_DISPLAY_NAMES,
  PATTERNS_BY_STYLE,
} from "../constants/patternDisplayNames";
import { useAuth } from "@/context/AuthContext";
import { dictionary, Language } from "@/utils/dictionary";
import { useVideoDraftStore } from "@/store/videoDraftStore";
import { VIDEO_DRAFT_STATUS } from "@/services/ads/videos/videoDraft.types";
import { ButtonFluxi } from "@/components/common/Buttons/ButtonFluxi/ButtonFluxi";

interface PreviewApprovalProps {
  /** Called when the user wants to go back to the studio without rejecting the draft. */
  onBack: () => void;
  /** Called after a successful approve OR reject — closes the modal. */
  onDone: () => void;
}

const MAX_WORDS_PER_SCRIPT_PART = 35;
const MAX_WORDS_TOTAL = MAX_WORDS_PER_SCRIPT_PART * 2;

function wordCount(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

/**
 * Phase 5.4: split inteligente del guion unificado en script_part_a / part_b
 * antes de mandarlo al backend.
 *
 * El usuario ve un solo textarea con todo el guion. Internamente el video
 * son 2 escenas (Kling V3 Pro renderea de a 5-10s) y el backend valida que
 * cada parte tenga ≤25 palabras. Acá hacemos el split en el cliente para
 * mantener la experiencia simple.
 *
 * Estrategia:
 *   1. Si el texto entero tiene ≤25 palabras, no es combo: parte A = todo,
 *      parte B = null.
 *   2. Si es combo, busca el signo de puntuación final (".", "!", "?") más
 *      cercano al punto medio del texto en palabras. Eso da un corte
 *      narrativo natural.
 *   3. Si no hay puntuación cerca del medio, divide por mitad de palabras.
 */
function splitScriptIntoParts(
  fullText: string,
  isCombo: boolean
): { partA: string; partB: string | null } {
  const trimmed = fullText.trim();
  if (!isCombo) {
    return { partA: trimmed, partB: null };
  }

  const words = trimmed.split(/\s+/);
  if (words.length === 0) return { partA: "", partB: "" };

  // 1. Buscar puntuación cerca del medio.
  const midpoint = Math.floor(words.length / 2);
  // ventana ±30% del medio para buscar puntuación
  const windowSize = Math.max(2, Math.floor(words.length * 0.3));
  const start = Math.max(0, midpoint - windowSize);
  const end = Math.min(words.length - 1, midpoint + windowSize);

  let bestSplitIndex = -1;
  let bestDistance = Number.MAX_SAFE_INTEGER;
  for (let i = start; i <= end; i++) {
    if (/[.!?]$/.test(words[i])) {
      const distance = Math.abs(i + 1 - midpoint);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestSplitIndex = i + 1; // split AFTER this word
      }
    }
  }

  // 2. Fallback: split por mitad de palabras
  if (bestSplitIndex === -1) {
    bestSplitIndex = midpoint;
  }

  const partA = words.slice(0, bestSplitIndex).join(" ");
  const partB = words.slice(bestSplitIndex).join(" ");
  return { partA, partB };
}

export function PreviewApproval({ onBack, onDone }: PreviewApprovalProps) {
  const { language } = useAuth();
  const lang = language as Language;

  const draft = useVideoDraftStore((s) => s.draft);
  const storeStatus = useVideoDraftStore((s) => s.storeStatus);
  const errorMessage = useVideoDraftStore((s) => s.errorMessage);
  const approve = useVideoDraftStore((s) => s.approve);
  const reject = useVideoDraftStore((s) => s.reject);
  const reset = useVideoDraftStore((s) => s.reset);
  const editScript = useVideoDraftStore((s) => s.editScript);
  const regenerateImage = useVideoDraftStore((s) => s.regenerateImage);
  const regenerateStyle = useVideoDraftStore((s) => s.regenerateStyle);

  const t = (key: string, fallback: string) =>
    dictionary[key]?.[lang] || fallback;

  // ── Local edit buffer (single unified script editor) ──
  // Internamente sigue siendo combo (script_part_a + script_part_b para el
  // dispatch a Kling), pero al usuario le mostramos UN solo texto fluido.
  // El split en partes lo hacemos al guardar con splitScriptIntoParts().
  const payload = draft?.director_payload;
  const isCombo = !!payload?.script_part_b;
  const initialScriptA = payload?.script_part_a ?? "";
  const initialScriptB = payload?.script_part_b ?? "";
  const initialFullScript = isCombo
    ? `${initialScriptA.trim()} ${initialScriptB.trim()}`.trim()
    : initialScriptA.trim();

  const [fullScriptDraft, setFullScriptDraft] = useState(initialFullScript);
  const [editingScript, setEditingScript] = useState(false);
  // Phase 5.4: el editor del estilo vive en el padre para que el footer del
  // modal pueda cambiar sus botones según el modo de edición.
  const [editingStyle, setEditingStyle] = useState(false);
  const [styleInstruction, setStyleInstruction] = useState("");
  // Phase 5.4 follow-up: instrucción opcional para regenerar imagen.
  // Se expande inline cuando el usuario clickea "con instrucción".
  const [imageInstructionOpen, setImageInstructionOpen] = useState(false);
  const [imageInstruction, setImageInstruction] = useState("");

  // Reset local buffer whenever the draft (from server) changes,
  // e.g. after a successful edit / regenerate / pattern change.
  useEffect(() => {
    setFullScriptDraft(initialFullScript);
    setEditingScript(false);
    setEditingStyle(false);
    setStyleInstruction("");
    setImageInstructionOpen(false);
    setImageInstruction("");
  }, [initialFullScript]);

  // Auto-close after a successful done state (post-reject).
  useEffect(() => {
    if (storeStatus === "done") {
      const tm = setTimeout(() => {
        reset();
        onDone();
      }, 1500);
      return () => clearTimeout(tm);
    }
  }, [storeStatus, reset, onDone]);

  // ─────────────── Loading / error / done / rendering / finished ───────────────

  if (storeStatus === "directing" || storeStatus === "regenerating_style" || (!draft && storeStatus !== "error")) {
    const isRegen = storeStatus === "regenerating_style";
    return (
      <DirectorLoader
        title={
          isRegen
            ? t("video-preview-regen-style-title", "Cambiando el estilo creativo")
            : t("video-preview-loading-title", "Creando tu preview")
        }
      />
    );
  }

  if (storeStatus === "error" || draft?.status === VIDEO_DRAFT_STATUS.ERROR) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center gap-4">
        <XCircle className="h-12 w-12 text-destructive" />
        <h2 className="text-xl font-semibold text-foreground">
          {t("video-preview-error-title", "No pudimos crear el preview")}
        </h2>
        <p className="text-sm text-muted-foreground max-w-md">
          {errorMessage ||
            draft?.error_message ||
            t(
              "video-preview-error-subtitle",
              "Algo falló durante la generación. Tus créditos fueron devueltos automáticamente."
            )}
        </p>
        <button
          type="button"
          onClick={() => {
            reset();
            onBack();
          }}
          className="mt-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
        >
          {t("video-preview-back-to-studio", "Volver al estudio")}
        </button>
      </div>
    );
  }

  if (storeStatus === "done") {
    const wasApproved = !!draft?.approved_at;
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center gap-4">
        <CheckCircle2
          className={
            wasApproved ? "h-12 w-12 text-green-500" : "h-12 w-12 text-muted-foreground"
          }
        />
        <h2 className="text-xl font-semibold text-foreground">
          {wasApproved
            ? t("video-preview-approved-title", "¡Aprobado!")
            : t("video-preview-rejected-title", "Cancelado")}
        </h2>
        <p className="text-sm text-muted-foreground max-w-md">
          {wasApproved
            ? t("video-preview-approved-subtitle", "Tu video se está creando.")
            : t("video-preview-rejected-subtitle", "Te devolvimos los créditos.")}
        </p>
      </div>
    );
  }

  // ── Data derivation for ready / rendering / finished ──
  if (!draft || !payload) return null;

  const productName = draft.product_name || "tu producto";
  const fullScript = initialFullScript;
  const isApproving = storeStatus === "approving";
  const isRejecting = storeStatus === "rejecting";
  const isEditingScriptInFlight = storeStatus === "editing_script";
  const isRegenImage = storeStatus === "regenerating_image";
  const isBusy = isApproving || isRejecting || isEditingScriptInFlight || isRegenImage;

  const regenCount = draft.regenerate_count ?? 0;
  const maxRegen = draft.max_regenerate_count ?? 3;
  const regenLeft = Math.max(0, maxRegen - regenCount);
  const canRegen = regenLeft > 0 && !isBusy;

  // ── Rendering state (post-approve) ──
  if (storeStatus === "rendering") {
    const baseImage = draft.base_image_url;
    return (
      <div className="flex flex-col h-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-foreground leading-tight">
              {t("video-render-title", "Estamos creando tu video")}
            </h2>
            <p className="text-sm text-muted-foreground leading-tight truncate max-w-[320px]">
              {productName}
            </p>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-6 flex flex-col items-center gap-6">
          {baseImage ? (
            <div className="relative w-full max-w-xs aspect-[9/16] rounded-2xl overflow-hidden border border-border bg-muted/20 shadow-lg">
              <img src={baseImage} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2">
                <Loader2 className="h-4 w-4 text-white animate-spin" />
                <span className="text-xs text-white/90 font-medium">
                  {t("video-render-progress", "Casi listo")}
                </span>
              </div>
            </div>
          ) : (
            <div className="w-full max-w-xs aspect-[9/16] rounded-2xl border border-border bg-muted/20 flex flex-col items-center justify-center gap-3">
              <Loader2 className="h-10 w-10 text-primary animate-spin" />
              <p className="text-sm text-muted-foreground">
                {t("video-render-warming", "Preparando tu escena")}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (storeStatus === "finished" && draft.video_url) {
    return (
      <div className="flex flex-col h-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-foreground leading-tight">
              {t("video-render-done-title", "Tu video está listo")}
            </h2>
            <p className="text-sm text-muted-foreground leading-tight truncate max-w-[320px]">
              {productName}
            </p>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-6 flex flex-col items-center justify-center gap-6">
          <div className="w-full max-w-xs aspect-[9/16] rounded-2xl overflow-hidden border border-border bg-black shadow-lg">
            <video
              src={draft.video_url}
              controls
              autoPlay
              playsInline
              className="w-full h-full object-cover"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border flex-shrink-0">
          <button
            type="button"
            onClick={() => {
              reset();
              onDone();
            }}
            className="px-6 py-2.5 rounded-lg text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-opacity shadow-sm"
          >
            {t("video-render-done-action", "Listo")}
          </button>
        </div>
      </div>
    );
  }

  // ─────────────── READY state — editable preview (60/40 split) ───────────────

  // Script editor: 1 textarea unificado, contador de palabras totales.
  // Al guardar, splitScriptIntoParts hace el split inteligente cliente-side
  // y mandamos las 2 partes al backend (que ya espera ese contrato).
  const totalWords = wordCount(fullScriptDraft);
  const wordLimit = isCombo ? MAX_WORDS_TOTAL : MAX_WORDS_PER_SCRIPT_PART;
  const scriptDirty = fullScriptDraft.trim() !== initialFullScript.trim();
  const scriptOverLimit = totalWords > wordLimit;
  const canSaveScript = scriptDirty && !scriptOverLimit && fullScriptDraft.trim().length > 0 && !isBusy;

  const handleSaveScript = async () => {
    const { partA, partB } = splitScriptIntoParts(fullScriptDraft, isCombo);
    // Validación final: si después del split una parte excede el límite,
    // el backend devuelve un error claro y el usuario verá el toast.
    await editScript(partA, partB);
  };

  const handleCancelEdit = () => {
    setFullScriptDraft(initialFullScript);
    setEditingScript(false);
  };

  const handleStartEditStyle = () => {
    setEditingStyle(true);
    setStyleInstruction("");
  };

  const handleCancelStyle = () => {
    setEditingStyle(false);
    setStyleInstruction("");
  };

  const handleSubmitStyle = async () => {
    const trimmed = styleInstruction.trim();
    setEditingStyle(false);
    setStyleInstruction("");
    await regenerateStyle(null, trimmed.length > 0 ? trimmed : null);
  };

  return (
    <div className="flex flex-col h-full max-h-[90vh] overflow-hidden">
      {/* Header — mismo patrón que el Ad Studio: thumbnail real del producto
          + título + nombre, X de cerrar a la derecha. La X reemplaza el
          texto "Cancelar" del footer (consume reject así no se quedan los
          créditos colgados). */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-background border border-border overflow-hidden flex-shrink-0">
            {draft.product_image_url ? (
              <img
                src={draft.product_image_url}
                alt={productName}
                className="w-full h-full object-contain p-0.5"
              />
            ) : (
              <div className="w-full h-full bg-muted/30" />
            )}
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-foreground leading-tight">
              {t("video-preview-title", "Tu preview está listo")}
            </h2>
            <p className="text-sm text-muted-foreground leading-tight truncate max-w-[280px]">
              {productName}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => reject()}
          disabled={isBusy}
          aria-label={t("video-preview-cancel", "Cancelar")}
          className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors disabled:opacity-50"
        >
          {isRejecting ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <X className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Body — 60/40 split, fondo con gradient sutil del onboarding.
          Phase 5.4: el footer del modal ya no existe — las acciones
          principales (Cancelar + Crear mi video) viven al fondo de la
          columna izquierda para no robarle alto a la imagen. */}
      <div className="flex flex-1 min-h-0 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-purple-50">
        {/* LEFT — image + "Otra imagen". 45% del ancho para que la
            imagen y el panel de edición tengan más aire. */}
        <div className="w-[45%] min-w-0 border-r border-border/50 p-6 flex flex-col items-center justify-center gap-5 overflow-y-auto">
          <AnimatePresence mode="wait">
            {draft.base_image_url ? (
              <motion.div
                key={draft.base_image_url}
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="relative w-full max-w-sm aspect-[9/16] rounded-2xl overflow-hidden bg-muted/20"
                style={{
                  boxShadow:
                    "0 20px 50px -15px hsl(240 79% 62% / 0.35), 0 0 0 1px hsl(240 79% 62% / 0.1)",
                }}
              >
                <img
                  src={draft.base_image_url}
                  alt=""
                  className="w-full h-full object-cover"
                />
                {isRegenImage && (
                  <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex flex-col items-center justify-center gap-3">
                    <div className="relative">
                      <Loader2 className="h-12 w-12 text-white animate-spin" />
                      <Sparkles className="h-5 w-5 text-white absolute inset-0 m-auto animate-pulse-glow" />
                    </div>
                    <p className="text-xs text-white/90 font-medium">
                      {t("video-preview-image-generating-overlay", "Generando nueva versión...")}
                    </p>
                  </div>
                )}
              </motion.div>
            ) : (
              <div className="w-full max-w-sm aspect-[9/16] rounded-2xl border border-border bg-muted/20 flex items-center justify-center">
                <Loader2 className="h-10 w-10 text-primary animate-spin" />
              </div>
            )}
          </AnimatePresence>

          {/* Acciones de imagen.
              - "Otra imagen" → regen rápida sin instrucción.
              - "con instrucción" → expande un panel inline (mismo patrón
                que EditPanel del builder) con título, textarea, chips de
                sugerencias y Cancelar/Aplicar.
              "Otra versión" (regen completo) vive en el top de la columna
              derecha. */}
          <div className="w-full max-w-sm flex flex-col items-center gap-2">
            <div className="flex items-center gap-2">
              <ActionPill
                icon={
                  <RefreshCw
                    className={`h-3.5 w-3.5 ${isRegenImage ? "animate-spin" : ""}`}
                  />
                }
                label={
                  isRegenImage
                    ? t("video-preview-image-generating", "Generando...")
                    : t("video-preview-image-other", "Otra imagen")
                }
                onClick={() => regenerateImage()}
                disabled={!canRegen || isRegenImage || imageInstructionOpen}
              />
              <button
                type="button"
                onClick={() => setImageInstructionOpen((v) => !v)}
                disabled={!canRegen || isRegenImage}
                className="text-xs font-medium text-primary hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t("video-preview-image-edit-toggle", "con instrucción")}
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              {regenLeft === 0
                ? t("video-preview-changes-exhausted", "Ya no hay cambios disponibles")
                : t("video-preview-changes-counter", "{used}/{max} cambios usados")
                    .replace("{used}", String(regenCount))
                    .replace("{max}", String(maxRegen))}
            </p>

            <AnimatePresence>
              {imageInstructionOpen && (
                <ImageInstructionPanel
                  value={imageInstruction}
                  onChange={setImageInstruction}
                  isLoading={isRegenImage}
                  onCancel={() => {
                    setImageInstructionOpen(false);
                    setImageInstruction("");
                  }}
                  onSubmit={() => {
                    const trimmed = imageInstruction.trim();
                    setImageInstructionOpen(false);
                    setImageInstruction("");
                    regenerateImage(trimmed.length > 0 ? trimmed : null);
                  }}
                  lang={lang}
                />
              )}
            </AnimatePresence>
          </div>

        </div>

        {/* RIGHT — editable content (55% del modal) */}
        <div className="w-[55%] min-w-0 flex flex-col overflow-hidden">
          {/* Sin save bar superior — los dos editores (personalidad y
              guión) tienen sus propias acciones DENTRO del card, abajo,
              pegadas al input que el usuario está modificando. */}
          <div className="flex-1 min-h-0 overflow-y-auto px-8 py-6 space-y-6">
            {/* Personalidad — read mode chip OR edit mode editor */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Sparkles className="h-3 w-3 text-primary" />
                {t("video-preview-style-section", "Personalidad")}
              </p>
              {editingStyle ? (
                <StyleEditor
                  currentPatternKey={payload.selected_pattern_key}
                  styleId={draft.style_id ?? "sassy-object"}
                  instruction={styleInstruction}
                  onInstructionChange={setStyleInstruction}
                  onCancel={handleCancelStyle}
                  onSubmit={handleSubmitStyle}
                  isBusy={isBusy}
                  lang={lang}
                />
              ) : (
                <StyleChipDisplay
                  currentPatternKey={payload.selected_pattern_key}
                  styleId={draft.style_id ?? "sassy-object"}
                  onEdit={handleStartEditStyle}
                  disabled={!canRegen || isBusy || editingScript}
                  lang={lang}
                />
              )}
            </div>

            {/* Guión del video */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                {t("video-preview-script-section", "Guión del video")}
              </p>

              {editingScript ? (
                <div className="rounded-xl border border-primary/40 bg-gradient-to-br from-primary/5 to-accent/5 p-5 space-y-4">
                  <textarea
                    value={fullScriptDraft}
                    onChange={(e) => setFullScriptDraft(e.target.value)}
                    rows={6}
                    autoFocus
                    className="w-full rounded-lg border border-border bg-background px-3.5 py-2.5 text-sm leading-relaxed focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-none"
                    placeholder={t("video-preview-script-placeholder", "Escribe el guión completo del video...")}
                  />
                  <div className="flex justify-end">
                    <span
                      className={`text-xs tabular-nums ${
                        scriptOverLimit
                          ? "text-red-500 font-semibold"
                          : "text-muted-foreground"
                      }`}
                    >
                      {totalWords}/{wordLimit} {t("video-preview-words", "palabras")}
                    </span>
                  </div>
                  {/* Acciones del editor del guión — dentro del card,
                      abajo, pegadas al textarea. Mismo patrón que el
                      StyleEditor. */}
                  <div className="flex items-center justify-end gap-2 pt-1">
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      disabled={isBusy}
                      className="px-3 py-1.5 rounded-full text-xs font-medium text-muted-foreground hover:bg-white/60 transition-colors disabled:opacity-50"
                    >
                      {t("video-preview-edit-cancel", "Cancelar")}
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveScript}
                      disabled={!canSaveScript}
                      className="px-4 py-2 rounded-full text-xs font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-1.5"
                    >
                      {isEditingScriptInFlight ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          {t("video-preview-edit-saving", "Guardando...")}
                        </>
                      ) : (
                        <>
                          <Pencil className="h-3.5 w-3.5" />
                          {t("video-preview-save-changes", "Guardar cambios")}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-border bg-background p-4 hover:border-primary/40 transition-colors">
                  <div className="flex items-start gap-3">
                    <p className="flex-1 text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                      {fullScript}
                    </p>
                    <button
                      type="button"
                      onClick={() => setEditingScript(true)}
                      disabled={isBusy || editingStyle}
                      aria-label={t("video-preview-edit-script-aria", "Editar guión")}
                      className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Otra versión — debajo del guión, "última opción". Se oculta
                cuando el usuario está en modo edit (script o personalidad)
                porque ahí ya tiene su propio flujo de regenerar. */}
            {!editingScript && !editingStyle && (
            <div className="rounded-xl border border-primary/20 bg-white/60 p-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-semibold text-foreground leading-tight">
                  {t(
                    "video-preview-other-version-hint",
                    "¿No te convence? Genera una versión totalmente nueva (guión + imagen)."
                  )}
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">
                  {regenLeft === 0
                    ? t("video-preview-changes-exhausted", "Ya no hay cambios disponibles")
                    : t("video-preview-changes-counter", "{used}/{max} cambios usados")
                        .replace("{used}", String(regenCount))
                        .replace("{max}", String(maxRegen))}
                </p>
              </div>
              <button
                type="button"
                onClick={() => regenerateStyle(null, null)}
                disabled={!canRegen}
                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-primary bg-primary/5 border border-primary/30 hover:bg-primary/10 hover:border-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Sparkles className="h-3.5 w-3.5" />
                {t("video-preview-other-version", "Otra versión")}
              </button>
            </div>
            )}
          </div>

          {/* Footer del panel derecho — barra inferior solo del 42% de
              la columna derecha. Costo arriba (sutil, sin pill), Cancelar
              + Crear mi video alineados a la derecha abajo. */}
          <div className="flex flex-col items-center gap-2 px-6 py-4 border-t border-border flex-shrink-0 bg-background">
            <p className="text-[11px] text-muted-foreground tabular-nums">
              {t("video-cost", "costo")}{" "}
              <span className="font-semibold text-[#4F46E5]">
                {draft.credits_charged ?? 0} {t("video-credits", "créditos")}
              </span>
            </p>
            <div className="flex items-center justify-center">
              <ButtonFluxi
                icon={
                  isApproving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )
                }
                text={
                  isApproving
                    ? t("video-preview-approving", "Creando...")
                    : t("video-preview-approve", "Crear mi video")
                }
                onClick={async () => {
                  // Phase 5.5: optimistic close. As soon as the approve
                  // succeeds (backend cobra créditos + transición a
                  // GENERATING_IMAGE), cerramos el modal y dejamos que la
                  // gallery loading card se haga cargo del feedback. Si
                  // approve falla (4xx/5xx, INSUFFICIENT_CREDITS, draft not
                  // ready, etc), el store deja storeStatus = "ready" o
                  // "error" y el modal sigue abierto mostrando el error o
                  // el preview de nuevo, así el usuario puede reaccionar.
                  const ok = await approve();
                  if (ok) {
                    reset();
                    onDone();
                  }
                }}
                disabled={isBusy || editingScript || editingStyle}
                isEffect={!isBusy && !editingScript && !editingStyle}
              />
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

// ─────────────────────────────────────────────────
// Sub-component: ActionPill
// ─────────────────────────────────────────────────
// Botón pill consistente con icono + label, usado para los 3 controles
// editables (guion, estilo, imagen). Mismo size/look para que sean
// predecibles y descubribles.

interface ActionPillProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

function ActionPill({ icon, label, onClick, disabled }: ActionPillProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium bg-white/80 backdrop-blur-sm border border-border hover:border-primary hover:bg-primary/5 hover:text-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-border disabled:hover:bg-white/80 disabled:hover:text-foreground shadow-sm"
    >
      {icon}
      {label}
    </button>
  );
}

// ─────────────────────────────────────────────────
// Sub-component: DirectorLoader
// ─────────────────────────────────────────────────
// Loader rico para cuando el director Gemini está corriendo. Mensajes
// rotativos cada 3s + Sparkles + shimmer overlay (mismo pattern que
// GeneratingPlaceholderCard del módulo de creatives).

const LOADING_MESSAGE_KEYS = [
  "video-preview-loading-msg-1",
  "video-preview-loading-msg-2",
  "video-preview-loading-msg-3",
  "video-preview-loading-msg-4",
  "video-preview-loading-msg-5",
];

function DirectorLoader({ title }: { title: string }) {
  const { language } = useAuth();
  const lang = language as Language;
  const [messageIdx, setMessageIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setMessageIdx((i) => (i + 1) % LOADING_MESSAGE_KEYS.length);
    }, 3000);
    return () => clearInterval(id);
  }, []);

  const currentMessage =
    dictionary[LOADING_MESSAGE_KEYS[messageIdx]]?.[lang] || "";

  // El modal entero ya es chico (max-w-md) mientras este loader está visible.
  // No necesitamos un card-in-modal — solo el contenido centrado.
  return (
    <div className="flex flex-col items-center justify-center text-center gap-5 px-8 py-12 h-full w-full bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="relative">
        <div
          className="absolute inset-0 rounded-full blur-2xl opacity-50"
          style={{ background: "var(--gradient-fluxi)" }}
        />
        <div className="relative w-16 h-16 rounded-full flex items-center justify-center bg-white shadow-lg">
          <Loader2 className="h-8 w-8 text-primary animate-spin" />
          <Sparkles className="absolute h-4 w-4 text-primary animate-pulse-glow" />
        </div>
      </div>
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      <AnimatePresence mode="wait">
        <motion.p
          key={messageIdx}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.4 }}
          className="text-xs text-muted-foreground"
        >
          {currentMessage}
        </motion.p>
      </AnimatePresence>
    </div>
  );
}

// ─────────────────────────────────────────────────
// Sub-components: StyleChipDisplay (read) + StyleEditor (edit)
// ─────────────────────────────────────────────────
//
// Phase 5.4 polish: separamos el chip en read mode y el editor en edit mode.
// El estado de edit vive en el padre (PreviewApproval), así el footer del
// modal puede mostrar los botones Cancelar / Generar nueva versión cuando
// el usuario está editando. El editor es solo presentación: chips de quick
// vibes + input.
//
// El backend recibe el texto en español neutral que el usuario tipea (o un
// quick vibe ya en español). El director Gemini lo usa como user_instruction
// para regenerar el preview entero (script + image).

const QUICK_VIBE_KEYS = [
  "video-preview-vibe-angry",
  "video-preview-vibe-sarcastic",
  "video-preview-vibe-less-dramatic",
  "video-preview-vibe-funny",
];

function getPatternMeta(currentPatternKey: string, styleId: string) {
  const stylePatterns = PATTERNS_BY_STYLE[styleId] ?? [];
  return (
    stylePatterns.find((p) => p.key === currentPatternKey) ?? {
      key: currentPatternKey,
      name: PATTERN_DISPLAY_NAMES[currentPatternKey] || currentPatternKey,
      description: "",
    }
  );
}

interface StyleChipDisplayProps {
  currentPatternKey: string;
  styleId: string;
  onEdit: () => void;
  disabled: boolean;
  lang: Language;
}

function StyleChipDisplay({
  currentPatternKey,
  styleId,
  onEdit,
  disabled,
  lang,
}: StyleChipDisplayProps) {
  const currentMeta = useMemo(
    () => getPatternMeta(currentPatternKey, styleId),
    [currentPatternKey, styleId]
  );
  const editAriaLabel =
    dictionary["video-preview-edit-style-aria"]?.[lang] || "Cambiar personalidad";

  return (
    <div className="rounded-xl border border-border bg-gradient-to-br from-primary/5 to-accent/5 p-4 hover:border-primary/40 transition-colors group">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
          <Sparkles className="h-4 w-4 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground">
            {currentMeta.name}
          </p>
          {currentMeta.description && (
            <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
              {currentMeta.description}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onEdit}
          disabled={disabled}
          aria-label={editAriaLabel}
          className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

interface StyleEditorProps {
  currentPatternKey: string;
  styleId: string;
  instruction: string;
  onInstructionChange: (value: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
  isBusy: boolean;
  lang: Language;
}

function StyleEditor({
  currentPatternKey,
  styleId,
  instruction,
  onInstructionChange,
  onCancel,
  onSubmit,
  isBusy,
  lang,
}: StyleEditorProps) {
  const t = (key: string, fallback: string) =>
    dictionary[key]?.[lang] || fallback;
  const currentMeta = useMemo(
    () => getPatternMeta(currentPatternKey, styleId),
    [currentPatternKey, styleId]
  );

  const promptLabel =
    dictionary["video-preview-style-prompt"]?.[lang] || "¿Cómo lo quieres ahora?";
  const inputPlaceholder =
    dictionary["video-preview-style-input-placeholder"]?.[lang] ||
    "o escríbelo tú: ej. más amenazante, menos serio...";

  return (
    <div className="rounded-xl border border-primary/40 bg-gradient-to-br from-primary/5 to-accent/5 p-5 space-y-4">
      {/* Encabezado: pattern actual */}
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
          <Sparkles className="h-4 w-4 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground">
            {currentMeta.name}
          </p>
          {currentMeta.description && (
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              {currentMeta.description}
            </p>
          )}
        </div>
      </div>

      {/* Warning de regeneración */}
      <div className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-800 leading-relaxed">
        {dictionary["video-preview-style-edit-warning"]?.[lang] ||
          "Cambiar la personalidad regenera el guión y la imagen, y usa 1 de tus 3 cambios."}
      </div>

      {/* Bloque de input — separado del header con divisor sutil.
          Label arriba, opciones rápidas como sugerencias, input principal
          al fondo. Cada bloque con su propio espacio. */}
      <div className="border-t border-primary/20 pt-4 space-y-4">
        <div>
          <label className="block text-sm font-semibold text-foreground mb-1">
            {promptLabel}
          </label>
          <p className="text-xs text-muted-foreground">
            {t(
              "video-preview-style-quickvibes-label",
              "Opciones rápidas"
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {QUICK_VIBE_KEYS.map((vibeKey) => {
            const vibeText = dictionary[vibeKey]?.[lang] || "";
            return (
              <button
                key={vibeKey}
                type="button"
                onClick={() => onInstructionChange(vibeText)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  instruction === vibeText
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-white text-foreground border-border hover:border-primary hover:bg-primary/5"
                }`}
              >
                {vibeText}
              </button>
            );
          })}
        </div>
        <input
          type="text"
          value={instruction}
          onChange={(e) => onInstructionChange(e.target.value)}
          placeholder={inputPlaceholder}
          autoFocus
          maxLength={140}
          className="w-full rounded-lg border border-border bg-background px-4 py-2.5 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none"
        />
      </div>

      {/* Acciones — pegado al input pero con su propio bloque visual */}
      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={isBusy}
          className="px-3 py-1.5 rounded-full text-xs font-medium text-muted-foreground hover:bg-white/60 transition-colors disabled:opacity-50"
        >
          {t("video-preview-edit-cancel", "Cancelar")}
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={isBusy || instruction.trim().length === 0}
          className="px-4 py-2 rounded-full text-xs font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-1.5"
        >
          <Sparkles className="h-3.5 w-3.5" />
          {t("video-preview-regen-action", "Aplicar y regenerar")}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────
// Sub-component: ImageInstructionPanel
// ─────────────────────────────────────────────────
//
// Inline expand para que el usuario opcionalmente le mande una instrucción
// extra al image generator. Mismo patrón visual que EditPanel del builder
// (ads/cretives/components/modals/EditPanel.tsx) — slide vertical con
// framer-motion, título, textarea, chips de sugerencias y Cancelar / Aplicar.
//
// La instrucción es opcional. Si el usuario aplica con el textarea vacío,
// equivale a "Otra imagen" normal (sin instruction). El backend la appendea
// al brief sin reemplazar el wrapper Pixar character ni nada del prompt
// original — es solo una capa extra de personalización.

const IMAGE_INSTRUCTION_SUGGESTION_KEYS = [
  "video-preview-image-suggestion-brighter",
  "video-preview-image-suggestion-darker",
  "video-preview-image-suggestion-closeup",
  "video-preview-image-suggestion-colorful",
];

interface ImageInstructionPanelProps {
  value: string;
  onChange: (value: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
  isLoading: boolean;
  lang: Language;
}

function ImageInstructionPanel({
  value,
  onChange,
  onCancel,
  onSubmit,
  isLoading,
  lang,
}: ImageInstructionPanelProps) {
  const t = (key: string, fallback: string) =>
    dictionary[key]?.[lang] || fallback;

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="w-full overflow-hidden"
    >
      <div className="rounded-xl bg-muted/30 border border-border/50 p-4 mt-2">
        <p className="text-sm font-semibold text-foreground mb-2">
          {t("video-preview-image-edit-title", "¿Qué quieres cambiar de la imagen?")}
        </p>

        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t(
            "video-preview-image-edit-placeholder",
            "Describe qué quieres cambiar (opcional)..."
          )}
          rows={2}
          maxLength={200}
          autoFocus
          disabled={isLoading}
          className="w-full resize-none rounded-lg border border-border/60 bg-background px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none mb-3 min-h-[56px]"
        />

        {/* Sugerencias preset — clickearlas llena el textarea */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {IMAGE_INSTRUCTION_SUGGESTION_KEYS.map((suggestionKey) => {
            const label = dictionary[suggestionKey]?.[lang] || "";
            const isActive = value === label;
            return (
              <button
                key={suggestionKey}
                type="button"
                onClick={() => onChange(label)}
                disabled={isLoading}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed ${
                  isActive
                    ? "border-primary/40 bg-primary/5 text-primary"
                    : "border-border/50 text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-muted-foreground hover:bg-muted/60 transition-colors disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
            {t("video-preview-edit-cancel", "Cancelar")}
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={isLoading}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-xs font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            <Pencil className={`h-3.5 w-3.5 ${isLoading ? "animate-pulse" : ""}`} />
            {t("video-preview-image-edit-apply", "Aplicar")}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
